import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, CheckCircle, AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { useMachineSetupStore } from '../../stores/machine-setup-store';
import { MachineSetupStep } from '../../types/machine-setup-types';
import { MachineTypeStep } from '../organisms/MachineTypeStep';
import { MachineDetailsStep } from '../organisms/MachineDetailsStep';
import { ConfigStep } from '../organisms/ConfigStep';
import { ReviewStep } from '../organisms/ReviewStep';
import { ResultStep } from '../organisms/ResultStep';
import { BulkCreateStep } from '../organisms/BulkCreateStep';
import { BulkResultStep } from '../organisms/BulkResultStep';
import { ThirdPartyStep } from '../organisms/ThirdPartyStep';
import attributeOptionsRaw from '../../data/attribute-options.json';

const ATTRIBUTE_OPTIONS = attributeOptionsRaw as Record<string, { codeId: number; label: string }[]>;

type PageMode = 'setup' | 'validate' | null;

const STEP_LABELS = ['Machine Type', 'Details', 'Configuration', 'Review'];

const HARDWARE_LABELS: Record<string, string> = {
  vpost:       'VPOST',
  media4_mini: 'Media 4 Mini',
  media4:      'Media 4',
  media5:      'Media 5',
  uno_mini:    'Uno Mini',
};

export function MachineSetupPage() {
  const [mode, setMode] = useState<PageMode>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [validateMachineId, setValidateMachineId] = useState('');
  const [validateCategory, setValidateCategory] = useState('');
  const [validateTypeKey, setValidateTypeKey] = useState('');
  const [machineInfoLoading, setMachineInfoLoading] = useState(false);
  const [machineInfoName, setMachineInfoName] = useState<string | null>(null);
  const [machineInfoError, setMachineInfoError] = useState<string | null>(null);
  // Inline attribute editing state
  const [editedValues, setEditedValues] = useState<Record<number, string>>({});
  const [applyingIds, setApplyingIds] = useState<Set<number>>(new Set());
  const [applyResults, setApplyResults] = useState<Record<number, 'ok' | 'error'>>({});
  const [fixingAll, setFixingAll] = useState(false);

  const [bulkPhase, setBulkPhase] = useState<null | 'setup' | 'result'>(null);

  const {
    currentStep,
    machineTypes,
    wizardData,
    serialValidation,
    creationResult,
    bulkCreateResult,
    machineValidationResult,
    error,
    loadMachineTypes,
    validateSerial,
    validateMachineConfig,
    createMachine,
    createMachineBulk,
    getMachineInfo,
    applyAttributes,
    loadManufacturers,
    createThirdPartyMachine,
    loadMachineGroups,
    setStep,
    setWizardData,
    clearSerialValidation,
    reset,
  } = useMachineSetupStore();

  const isLoadingTypes = useMachineSetupStore((s) => s.isLoadingTypes);
  const isValidatingSerial = useMachineSetupStore((s) => s.isValidatingSerial);
  const isCreating = useMachineSetupStore((s) => s.isCreating);
  const isCreatingBulk = useMachineSetupStore((s) => s.isCreatingBulk);
  const isValidatingConfig = useMachineSetupStore((s) => s.isValidatingConfig);

  // Load machine types on mount; also retry if entering validate mode with an empty list
  useEffect(() => {
    loadMachineTypes();
  }, [loadMachineTypes]);

  useEffect(() => {
    if (mode === 'validate' && machineTypes.length === 0) {
      loadMachineTypes();
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Validation panel: auto-detect hardware from machine ID ──────────────────

  useEffect(() => {
    const id = parseInt(validateMachineId, 10);
    if (!validateMachineId || isNaN(id) || validateMachineId.length < 6) {
      setMachineInfoName(null);
      setMachineInfoError(null);
      setValidateCategory('');
      setValidateTypeKey('');
      return;
    }

    setMachineInfoLoading(true);
    setMachineInfoName(null);
    setMachineInfoError(null);

    const timer = setTimeout(async () => {
      try {
        const info = await getMachineInfo(id);
        if (!info.isSuccess) {
          setMachineInfoError(info.message ?? 'Machine not found');
          setValidateCategory('');
          setValidateTypeKey('');
          return;
        }
        setMachineInfoName(info.machineName ?? null);

        // 1. Exact match by machineTypeId + machineModelId
        const exactMatch = machineTypes.find(
          (t) => t.machineTypeId === info.machineTypeId && t.machineModelId === info.machineModelId
        );
        if (exactMatch) {
          setValidateCategory(exactMatch.category);
          setValidateTypeKey(exactMatch.key);
          return;
        }

        // 2. Detect category from linked device info (DeviceFamily / BoardSerial)
        const family = info.deviceFamily?.toUpperCase() ?? '';
        const board  = info.boardSerial ?? '';
        let detectedCategory = '';
        if (family.includes('VPOS')) {
          detectedCategory = 'vpost';
        } else if (board.startsWith('P12') || family.includes('MEDIA') || family.includes('MEDIAS')) {
          detectedCategory = 'media4';
        } else if (info.machineTypeId) {
          // Fallback: narrow by machineTypeId only
          const byTypeId = machineTypes.find((t) => t.machineTypeId === info.machineTypeId);
          if (byTypeId) detectedCategory = byTypeId.category;
        }
        setValidateCategory(detectedCategory);
        setValidateTypeKey('');
      } catch {
        setMachineInfoError('Failed to fetch machine info');
      } finally {
        setMachineInfoLoading(false);
      }
    }, 600);

    return () => {
      clearTimeout(timer);
      setMachineInfoLoading(false);
    };
  }, [validateMachineId, machineTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Setup wizard navigation ─────────────────────────────────────────────────

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case MachineSetupStep.MachineType:
        return !!wizardData.machineTypeKey;
      case MachineSetupStep.MachineDetails:
        if (!wizardData.machineName?.trim() || (wizardData.actorId ?? 0) <= 0) return false;
        if (!wizardData.serialNumber?.trim()) return true;
        return !!(serialValidation?.deviceFound && !serialValidation.isAssigned && !serialValidation.isWrongDistributor);
      case MachineSetupStep.Configuration:
        return (wizardData.machineGroupId ?? 0) > 0;
      case MachineSetupStep.Review:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep === MachineSetupStep.Review) {
      createMachine();
      return;
    }
    setStep((currentStep + 1) as MachineSetupStep);
  };

  const handleBack = () => {
    if (currentStep === MachineSetupStep.MachineType && selectedCategory === null) {
      // At the category selection screen → go back to mode selector
      reset();
      setMode(null);
    } else if (currentStep === MachineSetupStep.MachineType && selectedCategory !== null) {
      // Inside a category (type list or third-party) → go back to category selection
      setSelectedCategory(null);
      setWizardData({ machineTypeKey: '', machineTypeName: '' });
    } else if (currentStep > MachineSetupStep.MachineType) {
      if (currentStep === MachineSetupStep.Configuration) {
        clearSerialValidation();
      }
      setStep((currentStep - 1) as MachineSetupStep);
    }
  };

  const handleResetSetup = () => {
    reset();
    setSelectedCategory(null);
    setMode(null);
  };

  // ── Step content ────────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (currentStep) {
      case MachineSetupStep.MachineType:
        if (isThirdPartyFlow) {
          return (
            <ThirdPartyStep
              onSubmit={createThirdPartyMachine}
              onLoadManufacturers={loadManufacturers}
              onLoadMachineGroups={async (actorId) => {
                const res = await loadMachineGroups(actorId);
                return res.machineGroups ?? [];
              }}
            />
          );
        }
        return (
          <MachineTypeStep
            machineTypes={machineTypes}
            selectedKey={wizardData.machineTypeKey ?? ''}
            selectedCategory={selectedCategory}
            isLoading={isLoadingTypes && machineTypes.length === 0}
            error={machineTypes.length === 0 && !isLoadingTypes ? error : null}
            onCategorySelect={setSelectedCategory}
            onSelect={(key, name) => {
              setWizardData({ machineTypeKey: key, machineTypeName: name });
              setStep(MachineSetupStep.MachineDetails);
            }}
          />
        );
      case MachineSetupStep.MachineDetails:
        return (
          <MachineDetailsStep
            wizardData={wizardData}
            serialValidation={serialValidation}
            isLoading={isValidatingSerial}
            onChange={setWizardData}
            onValidateSerial={validateSerial}
          />
        );
      case MachineSetupStep.Configuration:
        return <ConfigStep wizardData={wizardData} onChange={setWizardData} machineTypes={machineTypes} />;
      case MachineSetupStep.Review:
        return <ReviewStep wizardData={wizardData} isLoading={isCreating} />;
      case MachineSetupStep.Result:
        return (
          <ResultStep
            result={creationResult}
            error={error}
            onCreateAnother={handleResetSetup}
            onCreateSameSettings={() => setBulkPhase('setup')}
            onCreateDifferentParams={() => {
              // Keep type/name, reset to details step so user picks new name+serial
              const typeKey  = wizardData.machineTypeKey;
              const typeName = wizardData.machineTypeName;
              reset();
              setWizardData({ machineTypeKey: typeKey, machineTypeName: typeName });
              setStep(MachineSetupStep.MachineDetails);
            }}
          />
        );
    }
  };

  const isResultStep = currentStep === MachineSetupStep.Result;
  const isLastActionStep = currentStep === MachineSetupStep.Review;
  const isThirdPartyFlow = selectedCategory === 'third_party';
  const isAtRoot = currentStep === MachineSetupStep.MachineType;

  // ── Mode selector ───────────────────────────────────────────────────────────

  if (mode === null) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Machine Setup</h1>
          <p className="text-gray-500 mt-1">Create or validate EV charging machines in Lynx.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setMode('setup')}
            className="text-left rounded-xl border-2 border-gray-200 bg-white p-6 hover:border-primary hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Plus className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Machine Setup</h2>
            </div>
            <p className="text-sm text-gray-500">
              Create a new EV charging machine and apply a preset configuration.
            </p>
          </button>
          <button
            onClick={() => setMode('validate')}
            className="text-left rounded-xl border-2 border-gray-200 bg-white p-6 hover:border-primary hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Machine Validation</h2>
            </div>
            <p className="text-sm text-gray-500">
              Check an existing machine&apos;s live configuration against its expected template.
            </p>
          </button>
        </div>
      </div>
    );
  }

  // ── Validation panel ────────────────────────────────────────────────────────

  if (mode === 'validate') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => setMode(null)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Machine Validation</h1>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Validate Configuration</h2>
          <p className="text-sm text-gray-500 mb-5">
            Compare a machine&apos;s live Lynx attributes against its expected preset.
          </p>
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Machine ID <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={validateMachineId}
                  onChange={(e) => setValidateMachineId(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g. 282586184"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {machineInfoLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              {machineInfoName && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-green-700">
                  <Check className="h-3.5 w-3.5" />
                  {machineInfoName}
                </p>
              )}
              {machineInfoError && (
                <p className="mt-1.5 text-xs text-red-600">{machineInfoError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hardware <span className="text-red-500">*</span>
              </label>
              <select
                value={validateCategory}
                onChange={(e) => { setValidateCategory(e.target.value); setValidateTypeKey(''); }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring bg-white"
              >
                <option value="">Select hardware…</option>
                {Array.from(new Set(machineTypes.map((t) => t.category).filter(Boolean)))
                  .sort()
                  .map((cat) => (
                    <option key={cat} value={cat}>
                      {HARDWARE_LABELS[cat] ?? cat}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Machine Type <span className="text-red-500">*</span>
              </label>
              <select
                value={validateTypeKey}
                onChange={(e) => setValidateTypeKey(e.target.value)}
                disabled={!validateCategory}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select a machine type…</option>
                {machineTypes
                  .filter((t) => t.category === validateCategory)
                  .map((t) => (
                    <option key={t.key} value={t.key}>{t.displayName}</option>
                  ))}
              </select>
            </div>
            <Button
              onClick={() => {
                const id = parseInt(validateMachineId, 10);
                if (!isNaN(id) && validateTypeKey) validateMachineConfig(id, validateTypeKey);
              }}
              disabled={!validateMachineId || !validateCategory || !validateTypeKey || isValidatingConfig}
            >
              {isValidatingConfig ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Validating…</>
              ) : 'Validate'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {machineValidationResult && (() => {
          const machineId = parseInt(validateMachineId, 10);

          const applyOne = async (attrId: number) => {
            const mismatch = machineValidationResult.mismatches.find(m => m.deviceAttributeId === attrId)!;
            const isCodeBased = mismatch.expectedCodeId !== null && mismatch.expectedCodeId !== undefined;
            const codeId = isCodeBased
              ? (attrId in editedValues ? Number(editedValues[attrId]) : mismatch.expectedCodeId)
              : null;
            const value = isCodeBased ? mismatch.expectedValue : (editedValues[attrId] ?? mismatch.expectedValue);
            setApplyingIds(prev => new Set(prev).add(attrId));
            try {
              const res = await applyAttributes(machineId, [{
                deviceAttributeId: attrId,
                value,
                codeId,
              }]);
              setApplyResults(prev => ({ ...prev, [attrId]: res.isSuccess ? 'ok' : 'error' }));
            } catch {
              setApplyResults(prev => ({ ...prev, [attrId]: 'error' }));
            } finally {
              setApplyingIds(prev => { const s = new Set(prev); s.delete(attrId); return s; });
            }
          };

          const fixAll = async () => {
            setFixingAll(true);
            try {
              const attrs = machineValidationResult.mismatches.map(m => {
                const isCodeBased = m.expectedCodeId !== null && m.expectedCodeId !== undefined;
                return {
                  deviceAttributeId: m.deviceAttributeId,
                  value: isCodeBased ? m.expectedValue : (editedValues[m.deviceAttributeId] ?? m.expectedValue),
                  codeId: isCodeBased
                    ? (m.deviceAttributeId in editedValues ? Number(editedValues[m.deviceAttributeId]) : m.expectedCodeId)
                    : null,
                };
              });
              const res = await applyAttributes(machineId, attrs);
              const status = res.isSuccess ? 'ok' : 'error';
              const next: Record<number, 'ok' | 'error'> = {};
              machineValidationResult.mismatches.forEach(m => { next[m.deviceAttributeId] = status; });
              setApplyResults(next);
            } catch {
              const next: Record<number, 'ok' | 'error'> = {};
              machineValidationResult.mismatches.forEach(m => { next[m.deviceAttributeId] = 'error'; });
              setApplyResults(next);
            } finally {
              setFixingAll(false);
            }
          };

          return (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {machineValidationResult.configMatches ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-sm font-medium text-green-700">Configuration matches the template</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <span className="text-sm font-medium text-red-700">
                        {machineValidationResult.mismatches.length} attribute{machineValidationResult.mismatches.length !== 1 ? 's' : ''} differ from the template
                      </span>
                    </>
                  )}
                </div>
                {machineValidationResult.mismatches.length > 0 && (
                  <Button size="sm" onClick={fixAll} disabled={fixingAll}>
                    {fixingAll ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Applying…</> : 'Fix All'}
                  </Button>
                )}
              </div>

              {machineValidationResult.mismatches.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-medium text-gray-600 w-1/4">Attribute</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-600 w-1/4">Expected</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-600 w-1/4">Actual</th>
                      <th className="py-2 w-16" />
                    </tr>
                  </thead>
                  <tbody>
                    {machineValidationResult.mismatches.map((m) => {
                      const result = applyResults[m.deviceAttributeId];
                      const applying = applyingIds.has(m.deviceAttributeId);
                      const options = ATTRIBUTE_OPTIONS[String(m.deviceAttributeId)];
                      const isCodeBased = m.expectedCodeId !== null && m.expectedCodeId !== undefined;
                      // For code-based attrs we track selected codeId; for value-based we track string
                      const selectedCodeId = m.deviceAttributeId in editedValues
                        ? Number(editedValues[m.deviceAttributeId])
                        : (m.expectedCodeId ?? 0);
                      const displayValue = editedValues[m.deviceAttributeId] ?? m.expectedValue;
                      return (
                        <tr key={m.deviceAttributeId} className={[
                          'border-b border-gray-100',
                          result === 'ok' ? 'bg-green-50' : result === 'error' ? 'bg-red-50' : '',
                        ].join(' ')}>
                          <td className="py-2 pr-4 text-gray-700">
                            {m.attributeName ?? `#${m.deviceAttributeId}`}
                          </td>
                          <td className="py-2 pr-4">
                            {isCodeBased && options?.length ? (
                              <select
                                value={selectedCodeId}
                                onChange={(e) => setEditedValues(prev => ({ ...prev, [m.deviceAttributeId]: e.target.value }))}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40 bg-white"
                              >
                                {options.map(o => (
                                  <option key={o.codeId} value={o.codeId}>{o.label}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={displayValue}
                                onChange={(e) => setEditedValues(prev => ({ ...prev, [m.deviceAttributeId]: e.target.value }))}
                                className="w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                              />
                            )}
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs text-red-700">{m.actualValue}</td>
                          <td className="py-2 text-right">
                            {result === 'ok' ? (
                              <CheckCircle className="h-4 w-4 text-green-500 inline" />
                            ) : result === 'error' ? (
                              <AlertCircle className="h-4 w-4 text-red-500 inline" />
                            ) : (
                              <button
                                onClick={() => applyOne(m.deviceAttributeId)}
                                disabled={applying}
                                className="text-xs text-primary hover:underline disabled:opacity-50"
                              >
                                {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : 'Apply'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Bulk create flow ─────────────────────────────────────────────────────────

  if (currentStep === MachineSetupStep.Result && bulkPhase === 'setup') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Machine Setup</h1>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <BulkCreateStep
            wizardData={wizardData}
            isLoading={isCreatingBulk}
            onBack={() => setBulkPhase(null)}
            onSubmit={async (req) => {
              await createMachineBulk(req);
              setBulkPhase('result');
            }}
          />
        </div>
      </div>
    );
  }

  if (currentStep === MachineSetupStep.Result && bulkPhase === 'result') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Machine Setup</h1>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <BulkResultStep
            result={bulkCreateResult}
            error={error}
            onDone={handleResetSetup}
          />
        </div>
      </div>
    );
  }

  // ── Setup wizard ────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          {!isResultStep && (
            <button
              onClick={isAtRoot ? handleResetSetup : handleBack}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>
          )}
          <h1 className="text-2xl font-semibold text-gray-900">Machine Setup</h1>
        </div>
        {(selectedCategory || wizardData.machineTypeKey) && !isResultStep && (
          <nav className="flex items-center gap-1.5 mt-2 ml-0 text-sm text-gray-400 select-none">
            <span className="text-gray-500">Machine Setup</span>
            {selectedCategory && (
              <>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                <span className={wizardData.machineTypeKey ? 'text-gray-500' : 'text-gray-800 font-medium'}>
                  {HARDWARE_LABELS[selectedCategory] ?? selectedCategory}
                </span>
              </>
            )}
            {wizardData.machineTypeName && (
              <>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                <span className="text-gray-800 font-medium">{wizardData.machineTypeName}</span>
              </>
            )}
          </nav>
        )}
      </div>

      {/* Step indicator */}
      {!isResultStep && (
        <div className="flex items-center mb-8">
          {STEP_LABELS.map((label, index) => {
            const isDone = currentStep > index;
            const isCurrent = currentStep === index;
            return (
              <div key={label} className="flex items-center">
                <div className="flex items-center gap-2">
                  <div
                    className={[
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                      isDone
                        ? 'bg-primary text-primary-foreground'
                        : isCurrent
                        ? 'bg-primary/15 text-foreground ring-2 ring-primary'
                        : 'bg-gray-100 text-gray-400',
                    ].join(' ')}
                  >
                    {isDone ? <Check className="h-4 w-4" /> : index + 1}
                  </div>
                  <span
                    className={[
                      'text-xs font-medium hidden sm:inline',
                      isCurrent ? 'text-foreground' : isDone ? 'text-gray-600' : 'text-gray-400',
                    ].join(' ')}
                  >
                    {label}
                  </span>
                </div>
                {index < STEP_LABELS.length - 1 && (
                  <div className={['mx-3 h-px flex-1 min-w-[1.5rem]', isDone ? 'bg-primary' : 'bg-gray-200'].join(' ')} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Step content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div key={currentStep} className="animate-in fade-in-0 slide-in-from-right-4 duration-200">
          {renderStep()}
        </div>
      </div>

      {/* Global error */}
      {error && !isResultStep && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Navigation */}
      {!isResultStep && (
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isCreating}
          >
            Back
          </Button>
          {!isThirdPartyFlow && currentStep !== MachineSetupStep.MachineType && (
            <Button
              onClick={handleNext}
              disabled={!canGoNext() || isCreating}
            >
              {isLastActionStep ? (
                isCreating
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</>
                  : 'Create Machine'
              ) : 'Next'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
